/* eslint @typescript-eslint/no-explicit-any: "off" */

import env from "../../env"

import { RequestCancelledError } from "../../errors"

export const get = (
    path: string,
    args: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver,
): Promise<any> => {
    const api: Api.Api = {
        args,
        headers,
        method: "GET",
        path,
        receiver,
    }
    return createRequest(api)
}

export const getHTML = (
    path: string,
    args?: any,
    headers?: Headers,
    receiver: Api.Receiver = ReceiveText,
): Promise<any> => {
    return get(path, args, headers, receiver)
}

export const getJSON = (
    path: string,
    args?: any,
    headers?: Headers,
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    return get(path, args, headers, receiver)
}

export const getText = (
    path: string,
    args?: any,
    headers?: Headers,
    receiver: Api.Receiver = ReceiveText,
): Promise<any> => {
    return get(path, args, headers, receiver)
}

export const getXML = (
    path: string,
    args?: any,
    headers?: Headers,
    receiver: Api.Receiver = ReceiveXML,
): Promise<any> => {
    return get(path, args, headers, receiver)
}

export const patch = (
    path: string,
    args: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver,
): Promise<any> => {
    const api: Api.Api = {
        args,
        method: "PATCH",
        path,
        headers,
        receiver,
    }
    return createRequest(api)
}

export const patchJSON = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    headers.append("Content-Type", "application/json; charset=UTF-8")
    return patch(path, JSON.stringify(args), headers, receiver)
}

export const postBody = ({
    args,
    body,
    headers,
    path,
    receiver = ReceiveJSON,
}: Api.BaseRequestParametersWithBody): Promise<any> => {
    return post({ args, body, headers, path, receiver })
}

export const post = ({
    args,
    body,
    headers = new Headers(),
    path,
    receiver,
}: Api.BasePostRequestParameters): Promise<any> => {
    const api: Api.Api = {
        args,
        body,
        headers,
        method: "POST",
        path,
        receiver,
    }
    return createRequest(api)
}

export const postHTML = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    headers.append("Content-Type", "text/html; charset=UTF-8")
    return post({ path, args, headers, receiver })
}

export const postJSON = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    headers.append("Content-Type", "application/json; charset=UTF-8")
    return post({ path, args: JSON.stringify(args), headers, receiver })
}

export const postText = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    headers.append("Content-Type", "text/plain; charset=UTF-8")
    return post({ path, args, headers, receiver })
}

export const postXML = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    headers.append("Content-Type", "text/xml; charset=UTF-8")
    return post({ path, args, headers, receiver })
}

export const postVoid = (
    path: string,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveNothing,
): Promise<any> => {
    return post({ path, args: null, headers, receiver })
}

export const put = (
    path: string,
    args: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver,
): Promise<any> => {
    headers.append("accept", receiver.accept || "*/*")
    const api: Api.Api = {
        args,
        method: "PUT",
        path,
        headers,
        receiver,
    }
    return createRequest(api)
}

export const putJSON = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveJSON,
): Promise<any> => {
    headers.append("Content-Type", "application/json; charset=UTF-8")
    return put(path, JSON.stringify(args), headers, receiver)
}

export const remove = (
    path: string,
    args?: any,
    headers: Headers = new Headers(),
    receiver: Api.Receiver = ReceiveNothing,
): Promise<any> => {
    const api: Api.Api = {
        args,
        headers,
        method: "DELETE",
        path,
        receiver,
    }
    return createRequest(api)
}

function createRequest(api: Api.Api): Promise<any> {
    const credentials = "same-origin"
    const { method, path, args } = api

    let fullPath = `${env.applicationPath}/${path}`

    const headers = api.headers ?? new Headers()
    headers.append("pageGuid", env.feedback)
    headers.append("accept", api.receiver.accept || "*/*")

    let resultPromise: Promise<any> = Promise.resolve()
    if (__SLOW_NETWORK__ !== undefined) {
        resultPromise = resultPromise.then(() => {
            return new Promise((resolve) => {
                console.log(
                    `Waiting ${__SLOW_NETWORK__}ms before sending request...`,
                )
                setTimeout(resolve, __SLOW_NETWORK__)
            })
        })
    }
    resultPromise = resultPromise.then(() => {
        if (args && method !== "GET" && !api.body) {
            if (__DEBUG__) {
                console.log(`Calling API on ${fullPath} with payload:`, args)
            }
            return fetch(fullPath, {
                method,
                headers,
                body: args,
                credentials,
            })
        }
        if (args) {
            const queryParams = path.includes("?")
                ? "&"
                : "?" +
                  Object.entries(args)
                      .filter(
                          (arg: [string, any]) =>
                              !arg.contains(null) && !arg.contains(undefined),
                      )
                      .map((arg: [string, any]) =>
                          arg.map(encodeURIComponent).join("="),
                      )
                      .join("&")

            fullPath += queryParams.length > 1 ? queryParams : ""
        }
        if (__DEBUG__) {
            console.log(`Calling API on ${fullPath}`)
        }
        return fetch(fullPath, {
            method,
            headers,
            body: api.body || null,
            credentials,
        })
    })
    return resultPromise.then(api.receiver.handler).catch((error: unknown) => {
        if (isCancellationError(error)) {
            // Please note that on some browsers, CORS issues might be swallowed by the following.
            // However, as we still log to the console, it should still be obvious once the console is opened.
            console.error(
                `The request has been cancelled, for example by navigating while a request was executing: ${error}`,
            )
            throw new RequestCancelledError((error as TypeError).message)
        }
        throw error
    })
}

const isCancellationError = (error: unknown): boolean => {
    // Based on https://stackoverflow.com/a/70452078 , seemingly no other way to detect a cancelled request.
    const possibleErrorMessagesForCancelledRequest = [
        "Failed to fetch",
        "cancelled",
        "NetworkError when attempting to fetch resource.",
    ]
    if (error instanceof TypeError) {
        return possibleErrorMessagesForCancelledRequest.includes(error.message)
    }
    return false
}

export const defaultJSONParsing = (response: Response): any => {
    if (!response.ok) {
        throw response
    }
    if (response.status === 204) {
        return Promise.resolve({})
    }
    return response.json()
}

export const defaultTextParsing = (response: Response): any => {
    if (!response.ok) {
        throw response
    }
    if (response.status === 204) {
        return Promise.resolve("")
    }
    return response.text()
}

export const ReceiveJSON: Api.Receiver = {
    accept: "application/json",
    handler: defaultJSONParsing,
}
export const ReceiveXML: Api.Receiver = {
    accept: "text/xml",
    handler: defaultTextParsing,
}
export const ReceiveText: Api.Receiver = {
    accept: "*/*",
    handler: defaultTextParsing,
}
export const ReceiveNothing: Api.Receiver = {
    accept: "*/*",
    handler: (response: Response) => {
        if (!response.ok) {
            throw response
        }
    },
}
